import React from "react";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { OrangeTextButton } from "../../../common/OrangeTextButton";

interface SiteVideoWallPaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
  label?: string;
  variant?: "dark" | "light";
  size?: "default" | "large" | "xlarge";
}

export const SiteVideoWallPagination: React.FC<
  SiteVideoWallPaginationProps
> = ({
  page,
  totalPages,
  onPageChange,
  className = "",
  label,
  variant = "dark",
  size = "default",
}) => {
  const isXLarge = size === "xlarge";
  const isLarge = size === "large" || isXLarge;
  const textSizeClass = isXLarge
    ? "text-[16px]"
    : isLarge
      ? "text-[18px]"
      : "text-[11px]";
  const gapClass = isXLarge ? "gap-4" : isLarge ? "gap-4" : "gap-2";
  const textClass =
    variant === "light" ? "text-gray-500" : "text-[#8b97a8]";
  const buttonClass = `rounded border border-[#33404f] bg-[#1c2530] font-bold text-[#dfe6ef] disabled:cursor-default disabled:opacity-30 ${
    isXLarge
      ? "px-6 py-1.5 text-[22px]"
      : isLarge
        ? "px-5 py-1 text-[18px]"
        : "px-2.5 py-0.5 text-[11px]"
  }`;

  const arrowIconSx = isXLarge
    ? { fontSize: 26 }
    : isLarge
      ? { fontSize: 24 }
      : { fontSize: 18 };

  const orangeArrowButtonSx = {
    minWidth: "auto",
    px: isXLarge ? 1 : isLarge ? 0.5 : 0.25,
    py: isXLarge ? 0.5 : 0.25,
  };

  const renderArrowButton = (
    direction: "prev" | "next",
    disabled: boolean,
    onClick: () => void
  ) => {
    const Icon = direction === "prev" ? ArrowBackIosIcon : ArrowForwardIosIcon;

    if (variant === "light") {
      return (
        <OrangeTextButton
          text={<Icon sx={arrowIconSx} />}
          disabled={disabled}
          onClick={onClick}
          sx={orangeArrowButtonSx}
        />
      );
    }

    return (
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={buttonClass}
      >
        <Icon sx={arrowIconSx} />
      </button>
    );
  };

  if (totalPages <= 1) {
    return label ? (
      <div
        className={`font-mulish text-center ${textSizeClass} ${textClass} ${className}`}
      >
        {label}
      </div>
    ) : null;
  }

  return (
    <div
      className={`flex items-center justify-center font-mulish ${gapClass} ${textSizeClass} ${textClass} ${className}`}
    >
      {renderArrowButton("prev", page <= 0, () => onPageChange(page - 1))}
      <span>{label ?? `${page + 1} / ${totalPages}`}</span>
      {renderArrowButton("next", page >= totalPages - 1, () =>
        onPageChange(page + 1)
      )}
    </div>
  );
};
